/**
 * THE ATTACK IMPACT CLOCK (cast-sync #8c, owner 2026-10-01: "start the swing at send"). A client-truth weapon attack
 * presents at SEND — the actor at enqueue, a peer on the operation's first frame — instead of waiting for the server's
 * roll rows: the swing starts now, retimed in the scene so its measured strike (ATTACK_CLIP_IMPACT_SECONDS) lands on
 * `impactAt`, and the weapon's hit/whiff take lands its HIT there too. The later roll-row cue for that attack is then
 * skipped (it would swing a second time); its damage float waits for the same impact.
 * Presentation only — nothing here delays or decides gameplay.
 */
import { currentFt3dSceneWindow } from '../ft3d-hud/scene-frame';
import { ATTACK_IMPACT_MS, THROW_FLIGHT_MS, type AttackThrowPresentation } from './attack-presentation-timing.js';
export { ATTACK_IMPACT_MS, THROW_FLIGHT_MS, type AttackThrowPresentation };
/** How long a send-time presentation waits for its roll rows before the rows present on their own again. */
const ROLL_MATCH_WINDOW_MS = 10_000;
// Window-scoped (see shared/spell-cast-timing.ts): every bundle on the page must read the same impacts.
type Presentation = { impactAt: number; sentAt: number };
type ClockState = { byOperation: Map<string, number>; unclaimed: Map<string, Presentation[]>; claimed: Map<string, number> };
const shared: ClockState = ((globalThis as { __ftAttackImpactClock?: ClockState }).__ftAttackImpactClock
  ??= { byOperation: new Map<string, number>(), unclaimed: new Map<string, Presentation[]>(), claimed: new Map<string, number>() });
const byOperation = shared.byOperation;
/** attacker:target → send-time presentations not yet claimed by a roll, oldest first (rows arrive in send order). */
const unclaimed = shared.unclaimed;
/** roll_id → the impact its swing was presented on (every row of one roll shares it). */
const claimed = shared.claimed;
const pairKey = (attacker: string, target: string) => `${String(attacker).toUpperCase()}:${String(target).toUpperCase()}`;

/** The same viewer preference that gates every combat animation cue (ft3d-hud/damage-feedback.ts). */
function combatCuesEnabled(): boolean {
  try { return window.localStorage.getItem('ft3d.hud.floatingDamage') !== 'off'; } catch { return true; }
}

/** Epoch ms at which this operation's swing connects; fixed on first call. No visible swing → now. */
export function attackImpactAt(operationId: string, attackerId: string, now = Date.now(), flightMs = 0): number {
  const op = String(operationId).toUpperCase();
  const known = byOperation.get(op);
  if (known !== undefined) return known;
  let gesture = true;
  try { gesture = (globalThis as { ftCastGestureAvailable?: (id: string) => boolean }).ftCastGestureAvailable?.(String(attackerId)) !== false; } catch { /* detached scene */ }
  const at = now + (gesture ? ATTACK_IMPACT_MS : 0) + flightMs;
  byOperation.set(op, at);
  if (byOperation.size > 200) byOperation.delete(byOperation.keys().next().value!);
  return at;
}

/** Post the swing (and the target's hit / miss reaction) to the FT3D scene, timed to `impactAt`. */
export function presentAttackAtSend(operationId: string, attackerId: string, targetId: string, hit: boolean, impactAt: number, now = Date.now(), throwFlight?: AttackThrowPresentation): void {
  if (!combatCuesEnabled()) return;
  const scene = currentFt3dSceneWindow();
  // Only a presentation that reached a scene may later stand in for the roll rows' own cue.
  if (!scene) return;
  scene.postMessage({ type: 'ft3d-campaign-animation-cue-v1', payload: {
    schema: 'ft.ft3d.animation-cue.v1', rollId: `op:${String(operationId).toUpperCase()}`,
    sourceEntityId: String(attackerId).toUpperCase(), targetEntityId: String(targetId).toUpperCase(),
    kind: hit ? 'hit' : 'miss', sourceKind: throwFlight ? 'throw' : 'attack', impactAt,
    ...(throwFlight ? { throwFlight, releaseAt: impactAt - THROW_FLIGHT_MS } : {}),
  } }, window.location.origin);
  const key = pairKey(attackerId, targetId);
  const queue = (unclaimed.get(key) ?? []).filter(row => now - row.sentAt < ROLL_MATCH_WINDOW_MS);
  queue.push({ impactAt, sentAt: now });
  unclaimed.set(key, queue);
  if (unclaimed.size > 200) unclaimed.delete(unclaimed.keys().next().value!);
}

/**
 * The impact a roll's swing was already presented on at send, or null (present it from the rows as before). A new
 * roll_id claims the OLDEST unclaimed send-time presentation for its attacker → target, exactly once; every later row
 * of that roll gets the same answer.
 */
export function presentedAttackImpact(rollId: string, attackerId: string, targetId: string, now = Date.now()): number | null {
  const id = String(rollId);
  const known = claimed.get(id);
  if (known !== undefined) return known;
  const key = pairKey(attackerId, targetId);
  const queue = (unclaimed.get(key) ?? []).filter(row => now - row.sentAt < ROLL_MATCH_WINDOW_MS);
  const next = queue.shift();
  if (queue.length) unclaimed.set(key, queue); else unclaimed.delete(key);
  if (!next) return null;
  claimed.set(id, next.impactAt);
  if (claimed.size > 400) claimed.delete(claimed.keys().next().value!);
  return next.impactAt;
}
